import React, { useState, useCallback, useEffect } from 'react';
import { Image, Modal, Spin, Tooltip } from 'antd';
import { ArrowsAltOutlined, CheckOutlined } from '@ant-design/icons';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { ChatMessage } from '@nxsys/types/models/AiChat';
import type { Components } from 'react-markdown';
import {
  StyledMessageRow,
  StyledBubble,
  StyledCursor,
  StyledMarkdownBlock,
  StyledTableWrapper,
  StyledTableScroll,
  StyledExpandButton,
  StyledModalTableWrapper,
  StyledToolCallBar,
} from './index.styled';
import type { GroupPosition } from './index.styled';
import { stripTicketMarker } from './ticketProposal';

type Props = {
  message: ChatMessage;
  isStreaming?: boolean;
  groupPosition?: GroupPosition;
  activeToolCall?: { name: string; label: string } | null;
};

const TableWithExpand: React.FC<React.HTMLAttributes<HTMLTableElement>> = (props) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = useCallback(() => setExpanded(true), []);
  const handleClose = useCallback(() => setExpanded(false), []);

  return (
    <>
      <StyledTableWrapper>
        <StyledTableScroll>
          <table {...props} />
        </StyledTableScroll>
        <StyledExpandButton
          type="text"
          size="small"
          icon={<ArrowsAltOutlined />}
          onClick={handleExpand}
          aria-label="Expand table"
        />
      </StyledTableWrapper>
      <Modal
        open={expanded}
        onCancel={handleClose}
        footer={null}
        width="fit-content"
        styles={{
          body: { overflow: 'auto', maxHeight: '80vh', padding: '12px 24px' },
          content: { maxWidth: '90vw' },
        }}
      >
        <StyledModalTableWrapper>
          <table {...props} />
        </StyledModalTableWrapper>
      </Modal>
    </>
  );
};

// While streaming, an image/link markdown is built char-by-char: `![alt](https://...`
// or `[▶ Watch video](https://...`. Until the closing `)` arrives it is not valid
// markdown, so the raw URL (and, for embed links, the label) would flash as plain
// text before turning into an image/iframe. Hide an incomplete trailing image OR
// link token until complete. The leading `!` is optional so it covers both.
const INCOMPLETE_MEDIA_TAIL = /!?\[[^\]\n]*(\][^)\n]*(\([^)\n]*)?)?$/;
const hideIncompleteMedia = (text: string) => text.replace(INCOMPLETE_MEDIA_TAIL, '');

// Trusted embed hosts: render KB guide/video links as inline responsive iframes.
const getEmbedKind = (href: string): 'guide' | 'video' | null => {
  try {
    const host = new URL(href).hostname.toLowerCase();
    if (host.endsWith('scribehow.com')) return 'guide';
    if (
      host.endsWith('vimeo.com') ||
      host.endsWith('youtube.com') ||
      host.endsWith('youtube-nocookie.com') ||
      host.endsWith('youtu.be') ||
      host.endsWith('loom.com')
    )
      return 'video';
    return null;
  } catch {
    return null;
  }
};

// Stable components map (defined once) so memoized blocks aren't invalidated by
// a fresh components object on every render.
const mdComponents: Components = {
  table: TableWithExpand,
  a: ({ href, children, ...rest }) => {
    const embed = href ? getEmbedKind(href) : null;
    if (embed && href) {
      // Direct inline iframe. Block-level memoization keeps this block stable
      // (its own block, fixed position) so the iframe isn't remounted/reloaded.
      return (
        <span style={{ display: 'block', margin: '8px 0' }}>
          <iframe
            src={href}
            title={embed === 'video' ? 'video' : 'step-by-step guide'}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
            allow="fullscreen; picture-in-picture"
            style={{
              display: 'block',
              width: '100%',
              aspectRatio: embed === 'video' ? '16 / 9' : '5 / 4',
              border: 0,
              borderRadius: 8,
            }}
          />
        </span>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    );
  },
  img: ({ src, alt }) => (
    <Image
      src={src}
      alt={alt || 'screenshot'}
      loading="lazy"
      preview={{ mask: null }}
      wrapperStyle={{
        display: 'block',
        width: 'fit-content',
        maxWidth: '100%',
        margin: '8px 0',
      }}
      style={{
        display: 'block',
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 8,
        cursor: 'zoom-in',
      }}
    />
  ),
};

// Split streamed markdown into top-level blocks (by blank lines, keeping fenced
// code blocks intact). Completed blocks are byte-stable so they can be memoized
// and skip re-parsing; only the last (growing) block re-parses per token.
const splitBlocks = (md: string): string[] => {
  const blocks: string[] = [];
  let cur: string[] = [];
  let inFence = false;
  const flush = () => {
    const s = cur.join('\n').trim();
    if (s) blocks.push(s);
    cur = [];
  };
  for (const line of md.split('\n')) {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    if (!inFence && line.trim() === '') {
      flush();
      continue;
    }
    cur.push(line);
  }
  flush();
  return blocks;
};

// rehype plugin: wrap each word in <span class="penny-fade-word">. Runs AFTER
// sanitize so the spans survive. Combined with block memoization + React's
// positional reconciliation, only NEWLY streamed words mount → they fade in,
// already-shown words stay put (no replay) — a ChatGPT-style per-word fade.
const rehypeFadeWords = () => (tree: any) => {
  const walk = (node: any) => {
    if (!node || !Array.isArray(node.children)) return;
    if (node.tagName === 'code' || node.tagName === 'pre') return; // keep code intact
    const out: any[] = [];
    for (const child of node.children) {
      if (child.type === 'text') {
        for (const part of child.value.split(/(\s+)/)) {
          if (part === '') continue;
          if (/^\s+$/.test(part)) {
            out.push({ type: 'text', value: part });
          } else {
            out.push({
              type: 'element',
              tagName: 'span',
              properties: { className: ['penny-fade-word'] },
              children: [{ type: 'text', value: part }],
            });
          }
        }
      } else {
        walk(child);
        out.push(child);
      }
    }
    node.children = out;
  };
  walk(tree);
};

// One markdown block, memoized on its raw source — finished blocks skip
// re-parse/re-render entirely (perf + no flicker); words fade in as they stream.
const MarkdownBlock = React.memo(({ source }: { source: string }) => (
  <StyledMarkdownBlock>
    <Markdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize, rehypeFadeWords]}
      components={mdComponents}
    >
      {source}
    </Markdown>
  </StyledMarkdownBlock>
));
MarkdownBlock.displayName = 'MarkdownBlock';

const useAnimatedDots = (active: boolean, interval = 500) => {
  const [count, setCount] = useState(1);
  useEffect(() => {
    if (!active) { setCount(1); return; }
    const id = setInterval(() => setCount((c) => (c % 3) + 1), interval);
    return () => clearInterval(id);
  }, [active, interval]);
  return '.'.repeat(count);
};

// A ticket create/append confirmation message (written after the user confirms
// the ticket card). Rendered as the branded green success bubble (design view 2)
// instead of a plain assistant markdown bubble. Detected by the leading ✅ and a
// "ticket" mention so it also styles correctly after a reload (backend persists
// the same text).
const parseTicketSuccess = (
  content: string,
): { ticketNumber?: string; appended: boolean } | null => {
  const t = content.trim();
  if (!t.startsWith('✅') || !/ticket/i.test(t)) return null;
  const appended = /note (?:has been )?added|added your note|added to ticket/i.test(t);
  const ticketNumber = (t.match(/#\s*(\d+)/) || [])[1];
  return { ticketNumber, appended };
};

const TicketSuccessBubble: React.FC<{ ticketNumber?: string; appended: boolean }> = ({
  ticketNumber,
  appended,
}) => (
  <div
    style={{
      alignSelf: 'flex-start',
      width: '100%',
      display: 'flex',
      gap: 9,
      alignItems: 'flex-start',
      border: '1px solid #D9F5E5',
      background: '#F3FCF7',
      borderRadius: 12,
      padding: '12px 14px',
      boxSizing: 'border-box',
    }}
  >
    <span
      style={{
        flex: 'none',
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: '#16B364',
        display: 'grid',
        placeItems: 'center',
        marginTop: 1,
      }}
    >
      <CheckOutlined style={{ fontSize: 14, color: '#fff' }} />
    </span>
    <div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.5, color: '#23282E' }}>
      {appended ? (
        <>
          Note added to ticket{' '}
          {ticketNumber ? (
            <b style={{ fontFamily: "'JetBrains Mono', monospace" }}>#{ticketNumber}</b>
          ) : (
            'your support ticket'
          )}
          . The support team will see it.
        </>
      ) : (
        <>
          {ticketNumber ? (
            <>
              Ticket <b style={{ fontFamily: "'JetBrains Mono', monospace" }}>#{ticketNumber}</b>{' '}
              created.
            </>
          ) : (
            'Your support ticket has been created.'
          )}{' '}
          The support team will follow up.
        </>
      )}
    </div>
  </div>
);

const MessageBubble: React.FC<Props> = ({ message, isStreaming, groupPosition = 'single', activeToolCall }) => {
  const isUser = message.role === 'user';
  const timestamp = message.createdAt
    ? new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';
  const isThinking = isStreaming && !isUser && !message.content.trim();
  const dots = useAnimatedDots(!!isThinking);
  const baseContent = isUser ? message.content : stripTicketMarker(message.content);
  const displayContent =
    isStreaming && !isUser ? hideIncompleteMedia(baseContent) : baseContent;

  const ticketSuccess = !isUser && !isStreaming ? parseTicketSuccess(displayContent) : null;
  if (ticketSuccess) {
    return (
      <StyledMessageRow $isUser={false} $groupPosition={groupPosition}>
        <TicketSuccessBubble
          ticketNumber={ticketSuccess.ticketNumber}
          appended={ticketSuccess.appended}
        />
      </StyledMessageRow>
    );
  }

  return (
    <StyledMessageRow $isUser={isUser} $groupPosition={groupPosition}>
      <Tooltip
        title={timestamp}
        placement={isUser ? 'left' : 'right'}
        mouseEnterDelay={1}
        getPopupContainer={(node) => node.parentElement || document.body}
      >
        <StyledBubble $isUser={isUser} $groupPosition={groupPosition}>
        {isThinking ? (
          <StyledToolCallBar>
            <Spin size="small" />
            <span>{activeToolCall?.label || 'Thinking'}{dots}</span>
          </StyledToolCallBar>
        ) : isUser ? (
          message.content
        ) : (
          splitBlocks(displayContent).map((block, i) => (
            <MarkdownBlock key={i} source={block} />
          ))
        )}
        {isStreaming && !isUser && !isThinking && <StyledCursor />}
        </StyledBubble>
      </Tooltip>
    </StyledMessageRow>
  );
};

export default MessageBubble;
